import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { filter, finalize, tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';

import { CommonApiService } from '../../common-api.service';
import { SnackbarService } from '../../snackbar.service';
import { LoginService } from '../../login/login.service';
import { EmployeeInHandJob } from './production-transfer.interface';

@Component({
  selector: 'app-production-transfer-form',
  templateUrl: './production-transfer.form.component.html',
  styleUrls: ['./production-transfer.form.component.css'],
})
export class ProductionTransferFormComponent implements OnInit {
  @ViewChild('from_employee_id', { static: false }) from_employee_id!: MatSelect;
  @ViewChild('to_employee_id', { static: false }) to_employee_id!: MatSelect;

  isAdmin = false;
  loadingJobs = false;
  submitting = false;

  fromEmployeeSearchPage = 0;
  allFromEmployeesCount = 0;
  public allFromEmployees: any[] = [];
  public selectedFromEmployeeObject: any[] = [];
  public selectedFromEmployee: number;

  toEmployeeSearchPage = 0;
  allToEmployeesCount = 0;
  public allToEmployees: any[] = [];
  public selectedToEmployeeObject: any[] = [];
  public selectedToEmployee: number;

  inHandJobs: EmployeeInHandJob[] = [];

  form: FormGroup;

  constructor(
    private fb: FormBuilder,
    private api: CommonApiService,
    private router: Router,
    private snackbar: SnackbarService,
    private auth: LoginService,
  ) {
    this.form = this.fb.group({
      from_employee_id: [null, Validators.required],
      job_ids: [[], Validators.required],
      to_employee_id: [null, Validators.required],
      transfer_reason: ['', [Validators.required, Validators.maxLength(1000)]],
    });
  }

  ngOnInit(): void {
    const cu: any = this.auth.currentUserValue;
    this.isAdmin = !!cu?.user?.admin;
    if (!this.isAdmin) {
      this.snackbar.error('Only an administrator can perform this action');
      this.router.navigate(['/home']);
      return;
    }
    this.onLoadFromEmployeeList();
    this.onLoadToEmployeeList();
  }

  onFromEmployeeKey(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    const params: any = {
      active: true,
      filterCriteria: [{ field: 'category', operator: 'equals', value: 1 }],
    };

    if (value) {
      params.searchValue = value;
    } else {
      params.pageIndex = 0;
      params.pageSize = environment.defaultPageSizeOnScroll;
    }

    this.api.postAll('employee/list/display', params).subscribe((res: any) => {
      const newItems = (res.items || []).filter(
        (entity: any) => entity.id !== this.selectedFromEmployeeObject[0]?.id,
      );
      this.allFromEmployees = this.selectedFromEmployeeObject[0]
        ? [this.selectedFromEmployeeObject[0], ...newItems]
        : newItems;
      this.allFromEmployeesCount = res.rowCount || 0;
      this.fromEmployeeSearchPage = 1;
    });
  }

  onFromEmployeeScroll(event: any): void {
    if (!this.from_employee_id?.panel || !this.from_employee_id?.panel.nativeElement) {
      return;
    }

    if (event) {
      this.from_employee_id.panel.nativeElement.addEventListener('scroll', () => {
        if (!this.from_employee_id?.panel?.nativeElement) {
          return;
        }

        const tolerance = 10;
        if (
          Math.floor(this.from_employee_id.panel.nativeElement.scrollTop) + tolerance >=
          Math.floor(this.from_employee_id.panel.nativeElement.scrollHeight) -
            Math.floor(this.from_employee_id.panel.nativeElement.offsetHeight)
        ) {
          if (this.allFromEmployees.length >= this.allFromEmployeesCount) {
            return;
          }

          const params = {
            pageIndex: this.fromEmployeeSearchPage,
            pageSize: environment.defaultPageSizeOnScroll,
            active: true,
            filterCriteria: [{ field: 'category', operator: 'equals', value: 1 }],
          };

          this.api.postAll('employee/list/display', params).subscribe((res: any) => {
            res.items = (res.items || []).filter(
              (entity: any) => entity.id !== this.selectedFromEmployeeObject[0]?.id,
            );
            this.allFromEmployees.push(...res.items);
          });
          this.fromEmployeeSearchPage++;
        }
      });
    }
  }

  onLoadFromEmployeeList(): void {
    const params = {
      pageIndex: this.fromEmployeeSearchPage,
      pageSize: environment.defaultPageSizeOnScroll,
      active: true,
      filterCriteria: [{ field: 'category', operator: 'equals', value: 1 }],
    };

    this.api
      .postAll('employee/list/display', params)
      .pipe(
        tap((res: any) => {
          this.allFromEmployees.push(...res.items);
          this.allFromEmployeesCount = res.rowCount;
        }),
        filter(() => this.selectedFromEmployeeObject.length !== 0),
        tap(() => {
          const selectedFromEmployeeId = this.selectedFromEmployeeObject[0].id;
          this.allFromEmployees = this.allFromEmployees.filter(
            (employee) => employee.id !== selectedFromEmployeeId,
          );
          this.allFromEmployees.unshift(this.selectedFromEmployeeObject[0]);
        }),
        finalize(() => this.fromEmployeeSearchPage++),
      )
      .subscribe();
  }

  onToEmployeeKey(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    const params: any = {
      active: true,
      filterCriteria: [{ field: 'category', operator: 'equals', value: 1 }],
    };

    if (value) {
      params.searchValue = value;
    } else {
      params.pageIndex = 0;
      params.pageSize = environment.defaultPageSizeOnScroll;
    }

    this.api.postAll('employee/list/display', params).subscribe((res: any) => {
      const newItems = (res.items || []).filter(
        (entity: any) => entity.id !== this.selectedToEmployeeObject[0]?.id,
      );
      this.allToEmployees = this.selectedToEmployeeObject[0]
        ? [this.selectedToEmployeeObject[0], ...newItems]
        : newItems;
      this.allToEmployeesCount = res.rowCount || 0;
      this.toEmployeeSearchPage = 1;
    });
  }

  onToEmployeeScroll(event: any): void {
    if (!this.to_employee_id?.panel || !this.to_employee_id?.panel.nativeElement) {
      return;
    }

    if (event) {
      this.to_employee_id.panel.nativeElement.addEventListener('scroll', () => {
        if (!this.to_employee_id?.panel?.nativeElement) {
          return;
        }

        const tolerance = 10;
        if (
          Math.floor(this.to_employee_id.panel.nativeElement.scrollTop) + tolerance >=
          Math.floor(this.to_employee_id.panel.nativeElement.scrollHeight) -
            Math.floor(this.to_employee_id.panel.nativeElement.offsetHeight)
        ) {
          if (this.allToEmployees.length >= this.allToEmployeesCount) {
            return;
          }

          const params = {
            pageIndex: this.toEmployeeSearchPage,
            pageSize: environment.defaultPageSizeOnScroll,
            active: true,
            filterCriteria: [{ field: 'category', operator: 'equals', value: 1 }],
          };

          this.api.postAll('employee/list/display', params).subscribe((res: any) => {
            res.items = (res.items || []).filter(
              (entity: any) => entity.id !== this.selectedToEmployeeObject[0]?.id,
            );
            this.allToEmployees.push(...res.items);
          });
          this.toEmployeeSearchPage++;
        }
      });
    }
  }

  onLoadToEmployeeList(): void {
    const params = {
      pageIndex: this.toEmployeeSearchPage,
      pageSize: environment.defaultPageSizeOnScroll,
      active: true,
      filterCriteria: [{ field: 'category', operator: 'equals', value: 1 }],
    };

    this.api
      .postAll('employee/list/display', params)
      .pipe(
        tap((res: any) => {
          this.allToEmployees.push(...res.items);
          this.allToEmployeesCount = res.rowCount;
        }),
        filter(() => this.selectedToEmployeeObject.length !== 0),
        tap(() => {
          const selectedToEmployeeId = this.selectedToEmployeeObject[0].id;
          this.allToEmployees = this.allToEmployees.filter(
            (employee) => employee.id !== selectedToEmployeeId,
          );
          this.allToEmployees.unshift(this.selectedToEmployeeObject[0]);
        }),
        finalize(() => this.toEmployeeSearchPage++),
      )
      .subscribe();
  }

  onFromEmployeeChange(employeeId: number): void {
    this.selectedFromEmployee = employeeId;
    this.selectedFromEmployeeObject = this.allFromEmployees.filter(
      (employee) => employee.id === employeeId,
    );

    if (this.selectedToEmployee === employeeId) {
      this.selectedToEmployee = undefined as any;
      this.selectedToEmployeeObject = [];
    }

    this.form.patchValue({
      from_employee_id: employeeId || null,
      to_employee_id: this.selectedToEmployee || null,
      job_ids: [],
    });

    this.inHandJobs = [];
    if (!employeeId) {
      return;
    }
    this.loadingJobs = true;
    this.api
      .postAll('production-transfer/employee/in-hand', { employee_id: employeeId })
      .subscribe({
        next: (res: any) => {
          this.inHandJobs = res?.items || [];
          this.loadingJobs = false;
        },
        error: (err: any) => {
          this.loadingJobs = false;
          this.snackbar.error(err?.error?.message || 'Failed to load jobs');
        },
      });
  }

  onToEmployeeChange(employeeId: number): void {
    if (employeeId && employeeId === this.selectedFromEmployee) {
      this.selectedToEmployee = undefined as any;
      this.selectedToEmployeeObject = [];
      this.form.patchValue({ to_employee_id: null });
      return;
    }

    this.selectedToEmployee = employeeId;
    this.selectedToEmployeeObject = this.allToEmployees.filter(
      (employee) => employee.id === employeeId,
    );
    this.form.patchValue({ to_employee_id: employeeId || null });
  }

  toEmployeeOptions(): any[] {
    return this.allToEmployees.filter((employee) => employee.id !== this.selectedFromEmployee);
  }

  cancel(): void {
    this.router.navigate(['/production-transfer']);
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.snackbar.error('Please fill all required fields');
      return;
    }
    if (!this.inHandJobs.length) {
      this.snackbar.error('Selected employee has no in-progress jobs');
      return;
    }
    const payload = this.form.value;
    this.submitting = true;
    this.api.postAll('production-transfer/create', payload).subscribe({
      next: (res: any) => {
        this.submitting = false;
        this.snackbar.success(res?.message || 'Transfer recorded');
        if (res?.skipped?.length) {
          this.snackbar.warning(`${res.skipped.length} job(s) were skipped`);
        }
        this.router.navigate(['/production-transfer']);
      },
      error: (err: any) => {
        this.submitting = false;
        this.snackbar.error(err?.error?.message || 'Transfer failed');
      },
    });
  }
}
